// React and hooks
import React, { useState, useEffect, useCallback } from 'react'

// MUI components
import { LinearProgress } from '@mui/material'
import { GridRowModes, useGridApiRef } from '@mui/x-data-grid-pro'

// Common components
import Snackbar from '../common/Snackbar'
import DefaultToolbarContainer from '../common/kc-table/components/toolbar-container/DefaultToolbarContainer'
import { KcTable } from '../common/kc-table'
import { CONST_VARIANT } from '../common/kc-table/constants'
import { CustomNoRowsOverlay } from '../common/kc-table/components'
import { AddConfigButton } from '../common/AddConfigButton'
import PlaceholderText from '../common/PlaceholderText'

// Layouts
import Layout from '../../layouts/common/Layout'

// Feature-specific components
import EditConfigDialog from './EditConfigDialog'
import GlobalConfigTableFooter from './GlobalConfigTableFooter'
import DetailPanelContent from './DetailPanelContent'

// Hooks
import { useKeyValueFields } from '../../hooks/useKeyValueFields'
import useGlobalConfigApi from '../../hooks/useGlobalConfigApi'

// Utilities
import { guid, isJSON, validate } from '../../utils'
import { transformGlobalConfigData } from '../../utils/transformData'
import ToggleButtons from '../common/ToggleButtons'
import { AllInbox, Settings, SettingsApplications } from '@mui/icons-material'
import { VALIDATION_RULES } from '../../constants/constants'

/**
 * GlobalConfigList component is responsible for rendering and managing a list of global configurations.
 * It provides functionalities for adding, editing, deleting, and saving configurations,
 * as well as switching between key-value and JSON text modes for configuration values.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {Function} props.getColumns - A function to retrieve the columns for the table.
 * @param {number} [props.defaultPageSize=1000] - The default number of rows per page.
 * @param {Function} props.onSave - A callback function to handle saving a configuration.
 * @param {Function} props.onDelete - A callback function to handle deleting a configuration.
 * @param {number|string} props.maxHeight - The maximum height of the table.
import ErrorIcon from '@mui/icons-material/Error'
 *
 * @returns {JSX.Element} The rendered GlobalConfigList component.
 *
 * @example
 * <GlobalConfigList
 *   getColumns={getColumns}
 *   defaultPageSize={1000}
 *   onSave={handleSave}
 *   onDelete={handleDelete}
 *   maxHeight="500px"
 *   initialState={initialState}
 * />
 */
export default function GlobalConfigList({
  getColumns,
  defaultPageSize = 1000,
  onSave,
  onDelete,
  maxHeight,
  initialState,
}) {
  const pageNo = 0
  const pageSize = defaultPageSize

  const { data, isLoading, error, refetch } = useGlobalConfigApi(
    pageNo,
    pageSize
  )
  const {
    fields: editKeyValueFields,
    setFieldValue,
    setFields: setEditKeyValueFields,
  } = useKeyValueFields([])

  const [rows, setRows] = useState([])
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editConfig, setEditConfig] = useState(null)
  const [editValueMode, setEditValueMode] = useState('keyValue') // 'keyValue' (default), 'text'
  const [rowModesModel, setRowModesModel] = useState({})
  const [expandedRowIds, setExpandedRowIds] = useState(new Set([]))
  const [snackbar, setSnackbar] = React.useState(null)

  const [filter, setFilter] = useState('all')

  const apiRef = useGridApiRef()

  useEffect(() => {
    setRows(
      transformGlobalConfigData(data?.listOfConfigs || []).filter((config) => {
        if (filter === 'all') return true
        if (filter === 'port')
          return config.key?.toLowerCase().startsWith('port-')
        if (filter === 'other')
          return !config.key?.toLowerCase().startsWith('port-')
        return true
      })
    )
  }, [data?.listOfConfigs, filter])

  const handleCloseSnackbar = () => setSnackbar(null)

  const handleProcessRowUpdateError = React.useCallback((error) => {
    setSnackbar({ children: error.message, severity: 'error' })
  }, [])

  const handleClick = () => {
    const id = guid()
    setRows((oldRows) => [
      ...oldRows,
      { id, key: '', value: '', comment: '', isNew: true },
    ])
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'key' },
    }))
  }

  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true
  }

  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true
  }

  const handleEditClick = useCallback(
    (id) => () => {
      const config = (data?.listOfConfigs || []).find((c) => c.id === id)
      if (config) {
        setEditConfig({ ...config })
        if (isJSON(config.value)) {
          const parsed = JSON.parse(config.value)
          setEditValueMode('keyValue')
          setEditKeyValueFields(
            Object.entries(parsed).map(([key, value]) => ({
              key,
              value: String(value),
            }))
          )
        } else {
          setEditValueMode('text')
          setEditKeyValueFields([])
        }
        setEditDialogOpen(true)
      }
    },
    [data?.listOfConfigs, setEditKeyValueFields]
  )
  const handleEditDialogClose = () => {
    setEditDialogOpen(false)
    setEditConfig(null)
    setEditValueMode('keyValue')
    setEditKeyValueFields([])
  }

  const handleEditDialogChange = (field) => (event) => {
    let value = event.target.value
    if (field === 'encrypted') {
      value = event.target.checked
    }
    setEditConfig((prev) => ({ ...prev, [field]: value }))
  }

  const handleValueModeSwitch = (event) => {
    const checked = event.target.checked
    if (checked) {
      // Switch to JSON mode
      setEditValueMode('text')
      setEditConfig((prev) => ({
        ...prev,
        value: JSON.stringify(
          Object.fromEntries(
            editKeyValueFields.map(({ key, value }) => [key, value])
          ),
          null,
          2
        ),
      }))
    } else {
      // Switch to key-value mode
      let parsed = {}
      try {
        parsed = JSON.parse(editConfig?.value || '{}')
      } catch {
        parsed = {}
      }
      setEditValueMode('keyValue')
      setEditKeyValueFields(
        Object.entries(parsed).map(([key, value]) => ({
          key,
          value: String(value),
        }))
      )
    }
  }

  const handleKeyValueFieldChange = (idx, field) => (event) => {
    const value = event.target.value
    setFieldValue(idx, field, value)
  }

  const handleEditDialogSave = async () => {
    if (!editConfig) return
    let valueToSave = editConfig.value
    if (editValueMode === 'keyValue') {
      // Convert keyValue fields to JSON string
      const obj = {}
      editKeyValueFields.forEach(({ key, value }) => {
        if (key) obj[key] = value
      })
      valueToSave = JSON.stringify(obj, null, 2)
    }
    try {
      const response = await onSave({ ...editConfig, value: valueToSave })
      if (response.status === 200) {
        setSnackbar({ children: response.message, severity: 'success' })
        setEditDialogOpen(false)
        setEditConfig(null)
        setEditValueMode('auto')
        setEditKeyValueFields([])
        refetch()
      }
    } catch (error) {
      setSnackbar({ children: error?.message, severity: 'error' })
    }
  }

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };


  const handleDeleteClick = useCallback(
    (id) => async () => {
      try {
        const response = await onDelete(id)
        if (response.status === 200) {
          setSnackbar({
            children: response?.message,
            severity: 'success',
          })
          refetch()
        }
      } catch (error) {
        setSnackbar({ children: error?.message, severity: 'error' })
      }
      setRows(rows.filter((row) => row.id !== id))
    },
    [onDelete, refetch, rows]
  )

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })

    const editedRow = rows.find((row) => row.id === id)
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id))
    }
  }

  const processRowUpdate = async (newRow) => {
        const hasKeyError = validate(newRow.key, [
          VALIDATION_RULES.REQUIRED,
          VALIDATION_RULES.MUST_START_WITH_LETTER,
          VALIDATION_RULES.ONLY_LETTERS_NUMBERS_DASHES_UNDERSCORE,
        ])
        const hasValueError = validate(newRow.value, [
          VALIDATION_RULES.REQUIRED,
        ])

        if (hasKeyError !== true) {
          throw new Error(`Key: ${hasKeyError}`)
        }
        if (hasValueError !== true) {
          throw new Error(`Value: ${hasValueError}`)
        }

    
        const updatedRow = { ...newRow, isNew: false }
    
    return new Promise(async (resolve, reject) => {
      try {
        const response = await onSave(newRow)
        if (response.status === 200) {
          setSnackbar({
            children: response.message,
            severity: 'success',
          })
          setRows(
            rows.map((row) => (row?.id === newRow?.id ? updatedRow : row))
          )
          refetch()
          resolve(updatedRow)
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  const onRowClick = React.useCallback(
    (params) => {
      if (params.row.type === 'port') {
        apiRef.current?.toggleDetailPanel(params.id)
      }
    },
    [apiRef]
  )

  const columns = getColumns({
    onSave: handleSaveClick,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onCancel: handleCancelClick,
    getMode: (id) => rowModesModel[id]?.mode,
    apiRef,
  })

  const getDetailPanelHeight = React.useCallback(() => 'auto', [])

  const getDetailPanelContent = React.useCallback(
    ({ row }) => {
      if (row.type !== 'port') return null
      return (
        <DetailPanelContent
          row={row}
          listOfConfigs={data?.listOfConfigs}
          handleDeleteClick={handleDeleteClick}
          handleEditClick={handleEditClick}
        />
      )
    },
    [data?.listOfConfigs, handleEditClick, handleDeleteClick]
  )

  const getRowClassName = (params) => {
    if (expandedRowIds.has(params.id)) {
      return 'expanded-row'
    }
    return ''
  }
  if (error && error?.status !== 421) {
    return (
      <Layout.CenterWrapper>
        <PlaceholderText fontSize="2rem">{error?.error}</PlaceholderText>
      </Layout.CenterWrapper>
    )
  }
  return (
    <>
      <EditConfigDialog
        open={editDialogOpen}
        config={editConfig}
        valueMode={editValueMode}
        keyValueFields={editKeyValueFields}
        isLoading={isLoading}
        onClose={handleEditDialogClose}
        onChange={handleEditDialogChange}
        onValueModeSwitch={handleValueModeSwitch}
        onKeyValueFieldChange={handleKeyValueFieldChange}
        onRemoveKeyValueField={(idx) =>
          setEditKeyValueFields((fields) => fields.filter((_, i) => i !== idx))
        }
        onAddKeyValueField={() =>
          setEditKeyValueFields((fields) => [...fields, { key: '', value: '' }])
        }
        onSave={handleEditDialogSave}
      />
        <ToggleButtons
          size="small"
          sx={{ mb: 2 }}
          value={filter}
          onChange={(e, newValue) => setFilter(newValue)}
          exclusive
          groupAriaLabel="quick config filter"
          groupLabel="Quick Filters:"
          options={[
            {
              value: 'all',
              label: 'All',
              icon: AllInbox,
            },
            {
              value: 'port',
              label: 'Port',
              icon: Settings,
            },
            { value: 'other', label: 'Other', icon: SettingsApplications },
          ]}
        />
      <KcTable
        apiRef={apiRef}
        layout={CONST_VARIANT.KC_DEFAULT}
        maxHeight={maxHeight}
        rows={rows || []}
        columns={columns}
        editMode="row"
        getRowId={(row) => row.id}
        rowModesModel={rowModesModel}
        onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        onRowClick={onRowClick}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={getDetailPanelContent}
        loading={isLoading}
        hideFooterRowCount
        sortingOrder={['desc', 'asc']}
        initialState={initialState}
        components={{
          LoadingOverlay: LinearProgress,
          Toolbar: DefaultToolbarContainer,
          NoRowsOverlay: CustomNoRowsOverlay,
          NoResultsOverlay: CustomNoRowsOverlay,
          Footer: GlobalConfigTableFooter,
        }}
        componentsProps={{
          noRowsOverlay: {
            children: <AddConfigButton onClick={() => handleClick()} />,
            text: 'No Configs Available',
          },
          noResultsOverlay: { text: 'No Configs Found' },
          footer: { onAdd: handleClick },
        }}
        experimentalFeatures={{ newEditingApi: true }}
        open={editDialogOpen}
        config={editConfig}
        valueMode={editValueMode}
        keyValueFields={editKeyValueFields}
        onDetailPanelExpandedRowIdsChange={(newIds) => setExpandedRowIds(new Set(newIds))}
        getRowClassName={getRowClassName}
        isLoading={isLoading}
        onClose={handleEditDialogClose}
        onChange={handleEditDialogChange}
        onValueModeSwitch={handleValueModeSwitch}
        onKeyValueFieldChange={handleKeyValueFieldChange}
        onRemoveKeyValueField={(idx) =>
          setEditKeyValueFields((fields) => fields.filter((_, i) => i !== idx))
        }
        onAddKeyValueField={() =>
          setEditKeyValueFields((fields) => [...fields, { key: '', value: '' }])
        }
        onSave={handleEditDialogSave}
      />
      {!!snackbar && (
        <Snackbar
          open
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
          {...snackbar}
        ></Snackbar>
      )}
    </>
  )
}
